import fetch from 'isomorphic-fetch';

import { showErrorBar } from 'actions/errorBar';
import {
  clearMoveModalFolders,
  createNewFolderSuccess,
  updateFetchingMoveModalTopLevelFolder,
  updateMoveModalFolders,
  updateMoveModalTopLevelFolder,
} from 'actions/moveModal';
import { addNewFolderFromMoveModal } from 'actions/videosDashboard';
import { ERROR } from 'constants/error-severities';
import {
  SHARED_VIDEOS_FOLDER_NAME,
  SPECIAL_FOLDER_ID_SHARED_VIDEOS,
  SPECIAL_FOLDER_ID_TEAM_VIDEOS,
} from 'constants/folders';
import { HEADER_CONTENT_TYPE } from 'constants/http';
import { JSON as JSON_MIME } from 'constants/mimes';
import { FOLDER } from 'constants/videosDashboard';
import { getGraphQLClient } from 'utilities/graphql';
import * as sentry from 'utilities/sentry';

import GetMyVideosFolder from './GetMyVideosFolder.graphql';
import GetSharedFoldersOwned from './GetSharedFoldersOwned.graphql';
import GetSubFolders from './GetSubFolders.graphql';
import GetTeamVideosFolder from './GetTeamVideosFolder.graphql';
import getTopLevelSharedEntities from './GetTopLevelSharedEntities.graphql';
import { markEligibleElementAsCompleteForSelectedWorkspace } from '../components/videos-dashboard-ta/dashboard-onboarding/utilities';
import { ORGANIZE_FOLDERS_CARD_KEY } from '../../../shared/constants/workspaceOnboarding';
import {
  PRIVATE_WORKSPACE,
  SHARED_WORKSPACE,
  TEAM_WORKSPACE,
} from '../../../shared/constants/workspaces';

export function fetchSubFoldersCreator(folderId, organizationId) {
  return async (dispatch, getState) => {
    let parentIsSpecialFolder = false;
    let folders = [];
    const gqlClient = getGraphQLClient();

    try {
      if (
        [SHARED_WORKSPACE, PRIVATE_WORKSPACE, TEAM_WORKSPACE].includes(folderId)
      ) {
        dispatch(clearMoveModalFolders());
        dispatch(updateFetchingMoveModalTopLevelFolder(true));
      }

      if (folderId === SHARED_WORKSPACE) {
        // loading shared workspace top-level folders behaves a little different than
        // team/private workspace or if we are fetching subfolders, so we have to handle it separately here.

        dispatch(
          updateMoveModalTopLevelFolder({
            name: SHARED_VIDEOS_FOLDER_NAME,
            special_id: SPECIAL_FOLDER_ID_SHARED_VIDEOS,
          })
        );

        const [foldersOwned, foldersShared] = await Promise.all([
          gqlClient.query({
            query: GetSharedFoldersOwned,
            fetchPolicy: 'no-cache',
          }),
          gqlClient.query({
            query: getTopLevelSharedEntities,
            fetchPolicy: 'no-cache',
          }),
        ]);

        const sharedWithUser =
          foldersShared.data.sharedWorkspaceTopLevelEntitiesForUser
            .filter(entity => entity.entity_type === FOLDER)
            .map(({ folder }) => {
              return {
                id: folder.id,
                name: folder.name,
                hasSubFolders: folder.hasSubFolders,
                isTopLevel: true,
              };
            });
        const ownedByUser = foldersOwned.data.sharedFoldersOwned.map(folder => {
          return {
            id: folder.id,
            name: folder.name,
            hasSubFolders: folder.hasSubFolders,
            isTopLevel: true,
          };
        });

        folders = [...sharedWithUser, ...ownedByUser].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } else {
        if (folderId === PRIVATE_WORKSPACE) {
          parentIsSpecialFolder = true;

          const { data } = await gqlClient.query({
            query: GetMyVideosFolder,
          });

          folderId = data.myVideosFolder.id;

          dispatch(updateMoveModalTopLevelFolder(data.myVideosFolder));
        }

        if (folderId === TEAM_WORKSPACE) {
          parentIsSpecialFolder = true;

          const { data } = await gqlClient.query({
            query: GetTeamVideosFolder,
            variables: {
              organizationId,
            },
          });

          folderId = data.teamVideosFolder.id;

          dispatch(updateMoveModalTopLevelFolder(data.teamVideosFolder));
        }

        const { data } = await gqlClient.query({
          query: GetSubFolders,
          variables: { parentId: folderId, organizationId },
          fetchPolicy: 'no-cache',
        });

        if (parentIsSpecialFolder) {
          folders = data.subFolders.map(folder => {
            return {
              ...folder,
              isTopLevel: true,
            };
          });
        } else {
          const hierarchy = JSON.parse(
            JSON.stringify(getState().moveModal.folders)
          );
          const folderIndex = hierarchy.findIndex(
            folder => folder.id === folderId
          );

          hierarchy[folderIndex].children = [...data.subFolders];

          folders = [...hierarchy, ...data.subFolders];
        }
      }

      dispatch(updateMoveModalFolders(folders));
    } catch (err) {
      dispatch(updateFetchingMoveModalTopLevelFolder(false));
      sentry.logException(err, 'Unable to fetch sub-folders');
    }
  };
}

export function createFolderCreator(parentFolderId = null, folderName) {
  if (!folderName) {
    return;
  }

  return (dispatch, getState) => {
    const topLevelFolder = getState().moveModal.topLevelFolder;
    const workspaceRed = getState().workspace;

    const { root_folder_id } = getState().videos_dashboard;

    const localId = new Date().getTime();
    const body = JSON.stringify({
      parent_folder_id: parentFolderId,
      name: folderName || undefined,
      local_id: localId,
    });

    return fetch('/v1/folders', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body,
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error creating folder - HTTP response: ${resp.status}`
          );
        }

        return resp.json();
      })
      .then(folder => {
        dispatch(createNewFolderSuccess(folder));

        if (topLevelFolder.special_id === root_folder_id) {
          // if the user is making a folder in the move modal in the same speical folder
          // they are viewing in the dashboard, then we need to add the new folder
          // in the videos_dashboard reducer.
          dispatch(addNewFolderFromMoveModal(folder));
        }

        if (topLevelFolder.special_id === SPECIAL_FOLDER_ID_TEAM_VIDEOS) {
          markEligibleElementAsCompleteForSelectedWorkspace(
            dispatch,
            workspaceRed.workspaces,
            ORGANIZE_FOLDERS_CARD_KEY
          );
        }
      })
      .catch(err => {
        sentry.logException(err, 'error in create folder creator');
        dispatch(showErrorBar(ERROR, err.toString()));
      });
  };
}
